import { Alert, AlertColor, Snackbar as MuiSnackbar } from "@mui/material";
import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

interface SnackbarState {
  open: boolean;
  message: string;
  type: "success" | "warning" | "error" | "info";
}

interface SnackbarContextProps {
  snackbar: SnackbarState;
  openSnackbar: (message: string, type: SnackbarState["type"]) => void;
  closeSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextProps | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    type: "info",
  });

  const openSnackbar = useCallback((
    message: string,
    type: SnackbarState["type"] = "info"
  ) => {
    setSnackbar({ open: true, message, type });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({ snackbar, openSnackbar, closeSnackbar }),
    [snackbar, openSnackbar, closeSnackbar]
  );

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    closeSnackbar();
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <MuiSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.type as AlertColor}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </MuiSnackbar>
    </SnackbarContext.Provider>
  );
};
